import React from 'react';
import styles from './ConditionRow.module.css';
import {
  CATEGORY_OPTIONS,
  VARIABLE_OPTIONS,
  OPERATOR_OPTIONS,
  TIME_OPERATOR_OPTIONS,
  ALL_INDICATORS,
  TIMEFRAME_OPTIONS,
} from '../../../constants/strategy';

const ConditionRow = ({ condition, index, onChange, onRemove, isLast, config }) => {
  const handleChange = (field, value) => {
    const updated = { ...condition, [field]: value };

    // Reset dependent fields when category changes
    if (field === 'category') {
      updated.variable = '';
      updated.operator = '';
      updated.value = '';
    }

    onChange(index, updated);
  };

  // Merge API config with hardcoded fallback — API overrides indicator categories,
  // Time/Volume/Price stay from hardcoded constants
  const mergedVariableOptions = { ...VARIABLE_OPTIONS, ...(config?.variableOptions || {}) };
  const variables = mergedVariableOptions[condition.category] || [];
  const allIndicators = config?.allIndicators || ALL_INDICATORS;
  const isTimeCategory = condition.category === 'Time';
  const operators = isTimeCategory ? TIME_OPERATOR_OPTIONS : OPERATOR_OPTIONS;

  return (
    <div className={styles.rowWrapper}>
      <div className={styles.row}>
        {/* ─── Top Row: Category → Variable → TF → Operator ─── */}
        <div className={styles.topRow}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Category</label>
            <select
              className={styles.select}
              value={condition.category || ''}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              <option value="">Select...</option>
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Variable</label>
            <select
              className={styles.select}
              value={condition.variable || ''}
              onChange={(e) => handleChange('variable', e.target.value)}
              disabled={!condition.category}
            >
              <option value="">Select...</option>
              {variables.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* TF dropdown — hidden for Time category (time is not timeframe-dependent) */}
          {!isTimeCategory && (
            <div className={styles.field} style={{ maxWidth: 100, flex: 'none' }}>
              <label className={styles.fieldLabel}>TF</label>
              <select
                className={styles.select}
                value={condition.variableTimeframe || '1m'}
                onChange={(e) => handleChange('variableTimeframe', e.target.value)}
              >
                {TIMEFRAME_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Operator</label>
            <select
              className={`${styles.select} ${styles.operatorSelect}`}
              value={condition.operator || ''}
              onChange={(e) => handleChange('operator', e.target.value)}
            >
              <option value="">Select...</option>
              {operators.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ─── Bottom Row: Compare To + Remove ─── */}
        <div className={styles.bottomRow}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {isTimeCategory ? 'Time (HH:MM)' : 'Compare To'}
            </label>

            {isTimeCategory ? (
              /* Time input — simple HH:MM picker */
              <input
                type="time"
                className={styles.input}
                value={condition.value || ''}
                onChange={(e) => handleChange('value', e.target.value)}
              />
            ) : (
              /* Standard indicator/static comparison */
              <div className={styles.valueGroup}>
                <div className={styles.valueToggle}>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${condition.valueType === 'StaticValue' ? styles.toggleActive : ''}`}
                    onClick={() => handleChange('valueType', 'StaticValue')}
                  >
                    Static
                  </button>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${condition.valueType === 'IndicatorValue' ? styles.toggleActive : ''}`}
                    onClick={() => handleChange('valueType', 'IndicatorValue')}
                  >
                    Indicator
                  </button>
                </div>
                {condition.valueType === 'IndicatorValue' ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select
                      className={styles.select}
                      value={condition.value || ''}
                      onChange={(e) => handleChange('value', e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select...</option>
                      {allIndicators.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <select
                      className={styles.select}
                      value={condition.valueTimeframe || '1m'}
                      onChange={(e) => handleChange('valueTimeframe', e.target.value)}
                      style={{ width: 95, flex: 'none' }}
                      title="Compare indicator timeframe"
                    >
                      {TIMEFRAME_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <input
                    type="number"
                    className={styles.input}
                    placeholder="Enter value"
                    value={condition.value || ''}
                    onChange={(e) => handleChange('value', e.target.value)}
                  />
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className={styles.removeBtn}
            onClick={() => onRemove(index)}
            title="Remove condition"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>

      {!isLast && (
        <div className={styles.logicRow}>
          <button
            type="button"
            className={`${styles.logicBtn} ${condition.logicalOperator === 'AND' ? styles.logicActive : ''}`}
            onClick={() => handleChange('logicalOperator', 'AND')}
          >
            AND
          </button>
          <button
            type="button"
            className={`${styles.logicBtn} ${condition.logicalOperator === 'OR' ? styles.logicActive : ''}`}
            onClick={() => handleChange('logicalOperator', 'OR')}
          >
            OR
          </button>
        </div>
      )}
    </div>
  );
};

export default ConditionRow;
